/**
 * ヤギプライムの加入状態。紹介キャンペーンの対象判定に使う。
 *
 * 要件④のとおり、既存ヤギプライム会員はキャンペーン対象外で、**紹介者としても対象外**。
 * トップ・履歴書画面では加入状態を持っていないため、シェア導線を出すときに判定する。
 *
 * App.vue が認証確定時にすでに `is_subscribed/` を叩いて `$globalVar` を埋めているので、
 * そのレスポンスを `applyMembership` で流し込む。同じ uid の判定が入っていれば
 * `loadMembership` は再取得しないため、通常はリクエストが二重にならない。
 *
 * 判定できていない間は訴求を出さない運用にして、対象外の会員に誤って訴求しない。
 */
import { computed, ref } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { fetchIsSubscribed } from '@/services/yagiprime/prime-membership-client'
import type { IsSubscribedResponse } from '@/services/yagiprime/prime-membership-client'

const isPrimeMember = ref(false)
const isChecked = ref(false)
let checkedUid = ''

/** 解約予約済みは無料期間の付与先として扱えるため、加入済みには含めない */
function resolveIsPrimeMember(data: IsSubscribedResponse): boolean {
  return !!(data.is_subscribed && !data.is_cancel)
}

/**
 * すでに取得済みの `is_subscribed/` レスポンスを判定に反映する。
 * App.vue の認証確定ハンドラから呼ぶ（同じ内容を二度取りに行かないため）。
 */
export function applyMembership(
  uid: string,
  data: IsSubscribedResponse | null | undefined
): void {
  if (!uid || !data) return
  checkedUid = uid
  isPrimeMember.value = resolveIsPrimeMember(data)
  isChecked.value = true
}

export function usePrimeMembership() {
  const { whenAuthReady } = useAuth()

  async function loadMembership(): Promise<void> {
    const user = await whenAuthReady()
    const uid = user?.uid
    if (!uid) {
      // ログアウト時は前のユーザーの判定を持ち越さない
      checkedUid = ''
      isPrimeMember.value = false
      isChecked.value = false
      return
    }
    // App.vue 側の取得が先に入っていれば再取得しない
    if (checkedUid === uid && isChecked.value) return
    checkedUid = uid
    const data = await fetchIsSubscribed(uid)
    // 応答待ちの間にユーザーが変わったら捨てる
    if (checkedUid !== uid) return
    if (!data) {
      // 取得できなかったときは判定済みにしない（訴求も出さない）
      isPrimeMember.value = false
      isChecked.value = false
      return
    }
    isPrimeMember.value = resolveIsPrimeMember(data)
    isChecked.value = true
  }

  /** キャンペーン対象者か。判定できていない間は false（誤訴求を出さない） */
  const isCampaignEligible = computed(
    () => isChecked.value && !isPrimeMember.value
  )

  return { isPrimeMember, isChecked, isCampaignEligible, loadMembership }
}
